<template>
  <ModalComponent
    ref="modal"
    size="lg"
    submitButtonText=""
    :beforeClose="() => !saving"
  >
    <template #title>
      {{
        editingGroup
          ? $t("device_groups.edit_group")
          : $t("device_groups.new_group")
      }}
    </template>

    <template #body>
      <div class="d-flex flex-column gap-3 device-group-modal">
        <div>
          <label :for="nameId" class="form-label fw-semibold fs-9">
            {{ $t("device_groups.name_label") }}
          </label>
          <input
            :id="nameId"
            v-model="name"
            type="text"
            class="form-control"
            :maxlength="NAME_MAX_LENGTH"
            :placeholder="$t('device_groups.name_placeholder')"
          />
        </div>

        <div class="ds-segctrl align-self-start" role="tablist">
          <button
            type="button"
            role="tab"
            :aria-selected="mode === 'pick'"
            :class="{ 'is-on': mode === 'pick' }"
            @click="mode = 'pick'"
          >
            {{ $t("device_groups.pick_devices") }}
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="mode === 'paste'"
            :class="{ 'is-on': mode === 'paste' }"
            @click="mode = 'paste'"
          >
            {{ $t("device_groups.paste_device_ids") }}
          </button>
        </div>

        <template v-if="mode === 'pick'">
          <div
            v-if="!editingGroup && suggestions.length > 0"
            class="d-flex flex-wrap align-items-center gap-2"
          >
            <span class="fs-9 text-body-secondary">
              {{ $t("device_groups.start_from_address") }}
            </span>
            <button
              v-for="suggestion in suggestions"
              :key="suggestion.name"
              type="button"
              class="device-group-modal__suggestion"
              @click="startFromSuggestion(suggestion)"
            >
              {{ suggestion.name }}
              <span class="text-body-secondary ms-1">
                {{ suggestion.deviceIds.length }}
              </span>
            </button>
          </div>

          <div class="d-flex gap-2">
            <input
              v-model="search"
              type="search"
              class="form-control form-control-sm"
              :placeholder="$t('device_groups.search_placeholder')"
              :aria-label="$t('device_groups.search_placeholder')"
            />
            <select
              v-model="technology"
              class="form-select form-select-sm device-group-modal__technology"
              :aria-label="$t('device_groups.all_technologies')"
            >
              <option value="">
                {{ $t("device_groups.all_technologies") }}
              </option>
              <option v-for="tech in technologies" :key="tech" :value="tech">
                {{ translateTechnology(tech, t) }}
              </option>
            </select>
          </div>

          <div class="d-flex align-items-center fs-9">
            <span class="text-body-secondary">
              {{
                $t(
                  "device_groups.devices_count",
                  { count: matchingRows.length },
                  matchingRows.length
                )
              }}
            </span>
            <button
              type="button"
              class="btn btn-link btn-sm ms-auto"
              :disabled="unselectedMatchingIds.length === 0"
              @click="selectMatching"
            >
              {{
                $t("device_groups.select_matching", {
                  count: unselectedMatchingIds.length,
                })
              }}
            </button>
            <button
              type="button"
              class="btn btn-link btn-sm text-body-secondary"
              :disabled="selectedMatchingIds.length === 0"
              @click="deselectMatching"
            >
              {{ $t("device_groups.deselect") }}
            </button>
          </div>

          <ul class="list-unstyled mb-0 device-group-modal__list border">
            <li v-for="{ device, lockedBy } in matchingRows" :key="device.id">
              <label
                class="device-group-modal__row"
                :class="{
                  'is-on': selected.has(device.id),
                  'is-locked': lockedBy !== null,
                }"
              >
                <input
                  type="checkbox"
                  class="form-check-input mt-0"
                  :checked="selected.has(device.id)"
                  :disabled="lockedBy !== null"
                  :data-device-id="device.id"
                  @change="toggleDevice(device.id)"
                />
                <span class="device-group-modal__id">{{
                  device.device_eic
                }}</span>
                <span class="text-truncate text-body-secondary flex-grow-1">
                  {{ secondaryLabel(device) }}
                </span>
                <span
                  v-if="lockedBy !== null"
                  class="badge pill-inactive text-truncate device-group-modal__tag"
                >
                  {{ lockedBy }}
                </span>
                <span class="device-group-modal__power">
                  {{ totalPowerKw([device]) }}
                  {{ $t("common.kw") }}
                </span>
              </label>
            </li>
          </ul>
        </template>

        <template v-else>
          <textarea
            v-model="pasteText"
            rows="8"
            class="form-control device-group-modal__paste"
            :placeholder="$t('device_groups.paste_placeholder')"
            :aria-label="$t('device_groups.paste_device_ids')"
          ></textarea>
          <div
            v-if="pasted.tokenCount > 0"
            class="d-flex flex-wrap align-items-center gap-2 fs-9"
          >
            <span>
              {{
                $t("device_groups.paste_found", {
                  found: pasted.matched.length + pasted.inOtherGroup.length,
                  total: pasted.tokenCount,
                })
              }}
            </span>
            <span
              v-for="token in pasted.notFound"
              :key="`missing-${token}`"
              class="badge pill-attention"
            >
              {{ $t("device_groups.paste_not_found", { id: token }) }}
            </span>
            <span
              v-for="device in pasted.inOtherGroup"
              :key="`taken-${device.id}`"
              class="badge pill-inactive"
            >
              {{
                $t("device_groups.paste_in_group", {
                  id: device.device_eic,
                  group: otherGroupName(device),
                })
              }}
            </span>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm ms-auto"
              :disabled="newPastedIds.length === 0"
              @click="addPasted"
            >
              {{
                $t(
                  "device_groups.add_devices",
                  { count: newPastedIds.length },
                  newPastedIds.length
                )
              }}
            </button>
          </div>
        </template>

        <div
          v-if="errorMessage"
          class="alert alert-danger py-2 mb-0"
          role="alert"
        >
          {{ errorMessage }}
        </div>
      </div>
    </template>

    <template #footer>
      <div class="d-flex flex-wrap align-items-center gap-2 w-100">
        <button
          v-if="editingGroup && !confirmingDelete"
          type="button"
          class="btn btn-link btn-sm device-group-modal__delete"
          :disabled="saving"
          @click="confirmingDelete = true"
        >
          {{ $t("device_groups.delete_group") }}
        </button>
        <div
          v-else-if="editingGroup"
          class="d-flex align-items-center gap-2 fs-9"
        >
          <span>
            {{
              $t("device_groups.delete_confirm", { name: editingGroup.name })
            }}
          </span>
          <button
            type="button"
            class="btn btn-danger btn-sm"
            :disabled="saving"
            @click="deleteGroup"
          >
            {{ $t("device_groups.delete_group") }}
          </button>
          <button
            type="button"
            class="btn btn-link btn-sm"
            @click="confirmingDelete = false"
          >
            {{ $t("common.cancel") }}
          </button>
        </div>
        <div v-else class="fs-9">
          <div class="fw-semibold">{{ selectionSummary }}</div>
          <div class="text-body-secondary">
            {{ $t("device_groups.modal_hint") }}
          </div>
        </div>

        <div class="d-flex align-items-center gap-2 ms-auto">
          <span v-if="editingGroup" class="fw-semibold fs-9 me-2">
            {{ selectionSummary }}
          </span>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="saving"
            @click="close"
          >
            {{ $t("common.cancel") }}
          </button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="!canSave"
            @click="save"
          >
            {{
              editingGroup
                ? $t("device_groups.save_group")
                : $t("device_groups.create_group")
            }}
          </button>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { useBusy } from "@/composables/useBusyAction";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  deviceGroupErrorMessage,
  deviceIdsByGroup,
  devicesPowerSummary,
  isInOtherGroup,
  parsePastedDeviceIds,
  totalPowerKw,
  type AddressSuggestion,
} from "@/dashboard/production_strategy/device_groups/deviceGroupUtils";
import type { DeviceGroup } from "@/dashboard/production_strategy/device_groups/deviceGroupsTypes";
import { useDeviceGroupsStore } from "@/dashboard/production_strategy/device_groups/useDeviceGroupsStore";
import type { RegistryDevice } from "@/types";
import { translateTechnology } from "@/utils/technologies";
import { sortBy } from "lodash-es";
import { computed, ref, useId, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

/** Mirrors `DEVICE_GROUP_NAME_MAX_LENGTH` in `app/registry_devices/models/device_group_models.py`. */
const NAME_MAX_LENGTH = 128;

/**
 * Creates or edits a device group: its name and members, picked from the list or pasted as device
 * IDs. A device in another group is shown but can't be picked, since a device is in at most one
 * group. Open it with `openCreate` or `openEdit`; it saves through the device groups store itself.
 */
const props = withDefaults(
  defineProps<{
    companyId: number;
    devices: RegistryDevice[];
    /** "Start from address" chips, offered when creating. */
    suggestions?: AddressSuggestion[];
  }>(),
  { suggestions: () => [] }
);

const emit = defineEmits<{
  saved: [groupId: number];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const deviceGroupsStore = useDeviceGroupsStore();
const modal = useTemplateRef<InstanceType<typeof ModalComponent>>("modal");
const nameId = useId();

const editingGroup = ref<DeviceGroup | null>(null);
const name = ref("");
const selected = ref<Set<number>>(new Set());
const initialIds = ref<Set<number>>(new Set());
// The devices listed first: the selection as of opening or the last bulk add, so ticking a single
// row never moves it under the pointer.
const pinnedIds = ref<Set<number>>(new Set());
const mode = ref<"pick" | "paste">("pick");
const search = ref("");
const technology = ref("");
const pasteText = ref("");
const { busy: saving, run } = useBusy();
const errorMessage = ref<string | null>(null);
const confirmingDelete = ref(false);

const technologies = computed(() =>
  [...new Set(props.devices.map((device) => device.technology))]
    .filter(Boolean)
    .sort()
);

// Derived from the devices prop, so a refetch while the modal is open shows fresh devices and locks.
// A ticked row stays clickable even if it is locked now, so it can be unticked instead of failing Save.
const orderedRows = computed(() =>
  sortBy(
    props.devices.map((device) => ({
      device,
      lockedBy: selected.value.has(device.id) ? null : otherGroupName(device),
    })),
    [
      ({ device }) => (pinnedIds.value.has(device.id) ? 0 : 1),
      ({ lockedBy }) => (lockedBy === null ? 0 : 1),
      ({ device }) => device.device_eic,
    ]
  )
);

const matchingRows = computed(() => {
  const query = search.value.trim().toLowerCase();
  return orderedRows.value.filter(
    ({ device }) =>
      (!technology.value || device.technology === technology.value) &&
      (!query ||
        [device.device_eic, device.device_name, device.address].some((field) =>
          field?.toLowerCase().includes(query)
        ))
  );
});

const pickableMatchingIds = computed(() =>
  matchingRows.value
    .filter(({ lockedBy }) => lockedBy === null)
    .map(({ device }) => device.id)
);
const unselectedMatchingIds = computed(() =>
  pickableMatchingIds.value.filter((id) => !selected.value.has(id))
);
const selectedMatchingIds = computed(() =>
  pickableMatchingIds.value.filter((id) => selected.value.has(id))
);

const pasted = computed(() =>
  parsePastedDeviceIds(
    pasteText.value,
    props.devices,
    editingGroup.value?.id ?? null
  )
);
const newPastedIds = computed(() =>
  pasted.value.matched
    .map((device) => device.id)
    .filter((id) => !selected.value.has(id))
);

const selectedDevices = computed(() =>
  props.devices.filter((device) => selected.value.has(device.id))
);
const selectionSummary = computed(() =>
  devicesPowerSummary(selectedDevices.value, t)
);

const addedIds = computed(() =>
  [...selected.value].filter((id) => !initialIds.value.has(id))
);
const removedIds = computed(() =>
  [...initialIds.value].filter((id) => !selected.value.has(id))
);
const membersChanged = computed(
  () => addedIds.value.length > 0 || removedIds.value.length > 0
);

const canSave = computed(() => {
  if (saving.value || name.value.trim().length === 0) return false;
  if (!editingGroup.value) return selected.value.size > 0;
  return membersChanged.value || name.value.trim() !== editingGroup.value.name;
});

const otherGroupName = (device: RegistryDevice): string | null =>
  isInOtherGroup(device, editingGroup.value?.id ?? null)
    ? (device.device_group_name ?? "")
    : null;

const secondaryLabel = (device: RegistryDevice): string =>
  (device.device_name && device.device_name !== device.device_eic
    ? device.device_name
    : device.address) ?? "—";

const reset = (group: DeviceGroup | null, deviceIds: number[]) => {
  editingGroup.value = group;
  name.value = group?.name ?? "";
  selected.value = new Set(deviceIds);
  initialIds.value = new Set(deviceIds);
  mode.value = "pick";
  search.value = "";
  technology.value = "";
  pasteText.value = "";
  errorMessage.value = null;
  confirmingDelete.value = false;
  pinSelected();
};

const pinSelected = () => {
  pinnedIds.value = new Set(selected.value);
};

const open = () => {
  if (modal.value) modal.value.showModal = true;
};

const close = () => {
  if (modal.value) modal.value.showModal = false;
};

const toggleDevice = (deviceId: number) => {
  const next = new Set(selected.value);
  if (!next.delete(deviceId)) next.add(deviceId);
  selected.value = next;
};

const selectMatching = () => {
  selected.value = new Set([...selected.value, ...unselectedMatchingIds.value]);
};

const deselectMatching = () => {
  const removed = new Set(selectedMatchingIds.value);
  selected.value = new Set(
    [...selected.value].filter((id) => !removed.has(id))
  );
};

const addPasted = () => {
  selected.value = new Set([...selected.value, ...newPastedIds.value]);
  pinSelected();
};

const startFromSuggestion = (suggestion: AddressSuggestion) => {
  selected.value = new Set([...selected.value, ...suggestion.deviceIds]);
  if (!name.value.trim()) name.value = suggestion.name;
  search.value = "";
  pinSelected();
};

const handleError = (error: unknown) => {
  const message = deviceGroupErrorMessage(error, t);
  if (message === null) globalErrorHandler(error);
  else errorMessage.value = message;
};

const save = () =>
  run(async () => {
    errorMessage.value = null;
    const trimmedName = name.value.trim();
    try {
      let groupId: number;
      if (editingGroup.value) {
        groupId = editingGroup.value.id;
        await deviceGroupsStore.update(props.companyId, groupId, {
          name:
            trimmedName !== editingGroup.value.name ? trimmedName : undefined,
          add_device_ids: addedIds.value,
          remove_device_ids: removedIds.value,
        });
      } else {
        groupId = (
          await deviceGroupsStore.create(props.companyId, {
            name: trimmedName,
            device_ids: [...selected.value],
          })
        ).id;
      }
      globalSuccessHandler(
        t(
          editingGroup.value
            ? "device_groups.group_saved"
            : "device_groups.group_created",
          { name: trimmedName }
        ),
        ""
      );
      close();
      emit("saved", groupId);
    } catch (error) {
      handleError(error);
    }
  });

const deleteGroup = async () => {
  const group = editingGroup.value;
  if (!group) return;
  await run(async () => {
    try {
      await deviceGroupsStore.remove(props.companyId, group.id);
      globalSuccessHandler(
        t("device_groups.group_deleted", { name: group.name }),
        ""
      );
      close();
    } catch (error) {
      handleError(error);
    }
  });
  confirmingDelete.value = false;
};

/** Opens an empty "New group", or one seeded with devices (e.g. the Devices tab selection). */
const openCreate = (deviceIds: number[] = []) => {
  reset(null, deviceIds);
  open();
};

/** Opens the editor on a group with its current members. */
const openEdit = (group: DeviceGroup) => {
  reset(group, deviceIdsByGroup(props.devices)[group.id] ?? []);
  open();
};

defineExpose({ openCreate, openEdit });
</script>

<style scoped lang="scss">
.device-group-modal {
  font-size: 0.8rem;
}

.device-group-modal__suggestion {
  all: unset;
  cursor: pointer;
  padding: 3px 10px;
  border: 1px dashed var(--phoenix-border-color);
  border-radius: 999px;
  font-size: 0.72rem;

  &:hover {
    background: var(--phoenix-gray-50);
  }

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
  }
}

.device-group-modal__technology {
  width: 160px;
  flex-shrink: 0;
}

.device-group-modal__list {
  max-height: 340px;
  overflow-y: auto;
  border-radius: 8px;
}

.device-group-modal__row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 2.25rem;
  padding: 4px 12px;
  margin: 0;
  cursor: pointer;

  &:hover {
    background: var(--phoenix-gray-50);
  }

  &.is-on {
    background: var(--phoenix-primary-bg-subtle);
  }

  &.is-locked {
    cursor: default;
    color: var(--phoenix-quaternary-color);
  }
}

.device-group-modal__id {
  flex: 0 0 11.5rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device-group-modal__tag {
  max-width: 150px;
}

.device-group-modal__power {
  flex: 0 0 72px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.device-group-modal__paste {
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
}

.device-group-modal__delete {
  color: var(--phoenix-orange-700);
}
</style>
